import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Select, Switch } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { CURRENCY_OPTIONS } from './settingsApi';

let _uid = 0;
const makeId = () => `tmp-${Date.now()}-${_uid++}`;

/** Valid separator pairs presented as one "how amounts look" choice. */
export const NUMBER_FORMAT_PRESETS = [
  { value: '.|,', decimalSeparator: '.', thousandsSeparator: ',', label: '1,234,567.89' },
  { value: ',|.', decimalSeparator: ',', thousandsSeparator: '.', label: '1.234.567,89' },
  { value: ',| ', decimalSeparator: ',', thousandsSeparator: ' ', label: '1 234 567,89' },
  { value: '.|', decimalSeparator: '.', thousandsSeparator: '', label: '1234567.89' },
];

const presetKey = (settings) =>
  `${settings?.decimalSeparator || '.'}|${settings?.thousandsSeparator ?? ','}`;

function normalize(settings) {
  return {
    currencies: [...(settings?.currencies || [])],
    defaultCurrency: settings?.defaultCurrency || (settings?.currencies?.[0] ?? ''),
    numberFormat: NUMBER_FORMAT_PRESETS.some((p) => p.value === presetKey(settings))
      ? presetKey(settings)
      : NUMBER_FORMAT_PRESETS[0].value,
    paymentTerms: (settings?.paymentTerms || []).map((t) => ({
      key: t.id || makeId(), id: t.id || '', name: t.name || '', days: t.days ?? 0, isDefault: !!t.isDefault,
    })),
    paymentMethods: (settings?.paymentMethods || []).map((m) => ({
      key: m.id || makeId(), id: m.id || '', name: m.name || '', isDefault: !!m.isDefault,
    })),
    overrideCurrencies: !!settings?.overrideCurrencies,
    overridePaymentTerms: !!settings?.overridePaymentTerms,
    overridePaymentMethods: !!settings?.overridePaymentMethods,
  };
}

export default function CommercialTermsForm({ mode = 'org', initial, inheritFrom, saving, onSave }) {
  const t = useT();
  const [form, setForm] = useState(() => normalize(initial));
  const isProperty = mode === 'property';

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  // ---- currencies ----
  const availableCurrencies = useMemo(
    () => CURRENCY_OPTIONS.filter((c) => !form.currencies.includes(c.value)),
    [form.currencies],
  );

  const addCurrency = (code) => {
    if (!code || form.currencies.includes(code)) return;
    const currencies = [...form.currencies, code];
    update({ currencies, defaultCurrency: form.defaultCurrency || code });
  };

  const removeCurrency = (code) => {
    const currencies = form.currencies.filter((c) => c !== code);
    const defaultCurrency = form.defaultCurrency === code ? (currencies[0] ?? '') : form.defaultCurrency;
    update({ currencies, defaultCurrency });
  };

  // ---- payment terms ----
  const setTerm = (key, patch) =>
    update({ paymentTerms: form.paymentTerms.map((t) => (t.key === key ? { ...t, ...patch } : t)) });
  const setDefaultTerm = (key) =>
    update({ paymentTerms: form.paymentTerms.map((t) => ({ ...t, isDefault: t.key === key })) });
  const addTerm = () =>
    update({ paymentTerms: [...form.paymentTerms, { key: makeId(), id: '', name: '', days: 30, isDefault: false }] });
  const removeTerm = (key) =>
    update({ paymentTerms: form.paymentTerms.filter((t) => t.key !== key) });

  // ---- payment methods ----
  const setMethod = (key, patch) =>
    update({ paymentMethods: form.paymentMethods.map((m) => (m.key === key ? { ...m, ...patch } : m)) });
  const setDefaultMethod = (key) =>
    update({ paymentMethods: form.paymentMethods.map((m) => ({ ...m, isDefault: m.key === key })) });
  const addMethod = () =>
    update({ paymentMethods: [...form.paymentMethods, { key: makeId(), id: '', name: '', isDefault: false }] });
  const removeMethod = (key) =>
    update({ paymentMethods: form.paymentMethods.filter((m) => m.key !== key) });

  // ---- override toggles (property mode): seed from org defaults when enabling an empty section ----
  const toggleOverride = (flagKey, listKey, normalizer) => (on) => {
    const patch = { [flagKey]: on };
    if (on && form[listKey].length === 0 && inheritFrom) {
      patch[listKey] = normalizer(inheritFrom);
      if (listKey === 'currencies') patch.defaultCurrency = inheritFrom.defaultCurrency || inheritFrom.currencies?.[0] || '';
    }
    update(patch);
  };

  const seedCurrencies = (src) => [...(src.currencies || [])];
  const seedTerms = (src) =>
    (src.paymentTerms || []).map((t) => ({ key: makeId(), id: '', name: t.name, days: t.days, isDefault: t.isDefault }));
  const seedMethods = (src) =>
    (src.paymentMethods || []).map((m) => ({ key: makeId(), id: '', name: m.name, isDefault: m.isDefault }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      currencies: form.currencies,
      defaultCurrency: form.defaultCurrency || null,
      paymentTerms: form.paymentTerms
        .filter((t) => t.name.trim())
        .map((t) => ({ id: t.id || '', name: t.name.trim(), days: Number(t.days) || 0, isDefault: t.isDefault })),
      paymentMethods: form.paymentMethods
        .filter((m) => m.name.trim())
        .map((m) => ({ id: m.id || '', name: m.name.trim(), isDefault: m.isDefault })),
    };
    if (isProperty) {
      payload.overrideCurrencies = form.overrideCurrencies;
      payload.overridePaymentTerms = form.overridePaymentTerms;
      payload.overridePaymentMethods = form.overridePaymentMethods;
    } else {
      const preset = NUMBER_FORMAT_PRESETS.find((p) => p.value === form.numberFormat) || NUMBER_FORMAT_PRESETS[0];
      payload.decimalSeparator = preset.decimalSeparator;
      payload.thousandsSeparator = preset.thousandsSeparator;
    }
    onSave(payload);
  };

  const sectionEnabled = (flagKey) => !isProperty || form[flagKey];

  return (
    <form className="set-form" onSubmit={submit}>
      {/* Currencies */}
      <Card>
        <Card.Body>
          <div className="set-section__head">
            <div>
              <h3 className="set-section__title">{t('Currencies')}</h3>
              <p className="set-section__hint">{t('Currencies suppliers can transact in. One is the default.')}</p>
            </div>
            {isProperty && (
              <Switch
                checked={form.overrideCurrencies}
                onChange={toggleOverride('overrideCurrencies', 'currencies', seedCurrencies)}
                label={t('Override')}
              />
            )}
          </div>

          {!sectionEnabled('overrideCurrencies') ? (
            <p className="set-inherit">{t('Inherits the organization currencies.')}</p>
          ) : (
            <>
              <div className="set-add-row">
                <Select
                  options={availableCurrencies}
                  value=""
                  onChange={addCurrency}
                  placeholder={t('Add a currency…')}
                  ariaLabel={t('Add currency')}
                />
              </div>
              <div className="set-chips">
                {form.currencies.length === 0 && <span className="set-inherit">{t('No currencies added yet.')}</span>}
                {form.currencies.map((c) => {
                  const isDefault = c === form.defaultCurrency;
                  return (
                    <span key={c} className={`set-chip ${isDefault ? 'is-default' : ''}`.trim()}>
                      {c}
                      <button
                        type="button"
                        className={`set-chip__btn ${isDefault ? 'is-active' : ''}`.trim()}
                        title={isDefault ? t('Default currency') : t('Set as default')}
                        onClick={() => update({ defaultCurrency: c })}
                      >
                        <Icon.Star size={13} />
                      </button>
                      <button type="button" className="set-chip__btn" title={t('Remove')} onClick={() => removeCurrency(c)}>
                        <Icon.X size={13} />
                      </button>
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Number format (organization-wide) */}
      {!isProperty && (
        <Card>
          <Card.Body>
            <div className="set-section__head">
              <div>
                <h3 className="set-section__title">{t('Number format')}</h3>
                <p className="set-section__hint">
                  {t('How amounts are displayed everywhere — decimal and thousands separators.')}
                </p>
              </div>
            </div>
            <div className="set-add-row">
              <Select
                options={NUMBER_FORMAT_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
                value={form.numberFormat}
                onChange={(v) => update({ numberFormat: v })}
                ariaLabel={t('Number format')}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Payment terms */}
      <Card>
        <Card.Body>
          <div className="set-section__head">
            <div>
              <h3 className="set-section__title">{t('Payment terms')}</h3>
              <p className="set-section__hint">{t('Named terms (e.g. Net 30) with the number of days until due.')}</p>
            </div>
            {isProperty && (
              <Switch
                checked={form.overridePaymentTerms}
                onChange={toggleOverride('overridePaymentTerms', 'paymentTerms', seedTerms)}
                label={t('Override')}
              />
            )}
          </div>

          {!sectionEnabled('overridePaymentTerms') ? (
            <p className="set-inherit">{t('Inherits the organization payment terms.')}</p>
          ) : (
            <>
              <div className="set-rows">
                {form.paymentTerms.map((term) => (
                  <div key={term.key} className="set-row">
                    <Input value={term.name} onChange={(e) => setTerm(term.key, { name: e.target.value })} placeholder="Net 30" aria-label={t('Term name')} />
                    <Input type="number" value={term.days} onChange={(e) => setTerm(term.key, { days: e.target.value })} placeholder={t('Days')} aria-label={t('Days')} />
                    <button
                      type="button"
                      className={`set-row__default ${term.isDefault ? 'is-default' : ''}`.trim()}
                      onClick={() => setDefaultTerm(term.key)}
                    >
                      {term.isDefault && <Icon.CircleCheck size={13} />} {term.isDefault ? t('Default') : t('Set default')}
                    </button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeTerm(term.key)} aria-label={t('Remove term')}>
                      <Icon.Trash size={15} />
                    </Button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 'var(--space-3)' }}>
                <Button type="button" variant="secondary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={addTerm}>
                  {t('Add term')}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Payment methods */}
      <Card>
        <Card.Body>
          <div className="set-section__head">
            <div>
              <h3 className="set-section__title">{t('Payment methods')}</h3>
              <p className="set-section__hint">{t('How invoices to suppliers are typically settled.')}</p>
            </div>
            {isProperty && (
              <Switch
                checked={form.overridePaymentMethods}
                onChange={toggleOverride('overridePaymentMethods', 'paymentMethods', seedMethods)}
                label={t('Override')}
              />
            )}
          </div>

          {!sectionEnabled('overridePaymentMethods') ? (
            <p className="set-inherit">{t('Inherits the organization payment methods.')}</p>
          ) : (
            <>
              <div className="set-rows">
                {form.paymentMethods.map((m) => (
                  <div key={m.key} className="set-row set-row--method">
                    <Input value={m.name} onChange={(e) => setMethod(m.key, { name: e.target.value })} placeholder={t('Bank transfer')} aria-label={t('Method name')} />
                    <button
                      type="button"
                      className={`set-row__default ${m.isDefault ? 'is-default' : ''}`.trim()}
                      onClick={() => setDefaultMethod(m.key)}
                    >
                      {m.isDefault && <Icon.CircleCheck size={13} />} {m.isDefault ? t('Default') : t('Set default')}
                    </button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeMethod(m.key)} aria-label={t('Remove method')}>
                      <Icon.Trash size={15} />
                    </Button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 'var(--space-3)' }}>
                <Button type="button" variant="secondary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={addMethod}>
                  {t('Add method')}
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <div className="set-form__actions">
        <Button type="submit" variant="primary" loading={saving}>{t('Save changes')}</Button>
      </div>
    </form>
  );
}
